import { notification } from 'antd';
import { put, select, takeLatest, delay, all } from 'redux-saga/effects';
import { taskActions as actions } from '.';
import { mapActions } from '../../Map/slice';
import taskApi from '../taskApi';
import {
  IEditTaskPayload,
  IFieldProgress,
  IFieldProgressWithProgressArea,
  IGetFieldAllTaskListPayload,
  IGetTaskDetailsPayload,
  IGetTaskListParams,
  IGetTaskListPayload,
  IGetTaskPlannedResourcesPayload,
  IGetTaskProgressPayload,
  IGetTaskUsedResourcesPayload,
  IRoutesWithoutTaskFilters,
  ITask,
  ITaskAvailableResource,
  ITaskAvailableResourceOption,
  ITaskCategory,
  ITaskListResponse,
  ITaskPlannedResource,
  ITaskPlannedResourceResponseItem,
  ITasksTableItem,
  ITaskUsedResource,
  ITaskUsedResourceResponseItem,
  IUpdateTaskProgressPayload,
} from './types';
import { PayloadAction } from '@reduxjs/toolkit';
import { globalActions } from 'app/containers/Global/slice';
import { RouteConstants } from 'app/routes';
import i18next from 'i18next';
import { IFieldParameters } from '../../Map/slice/types';
import {
  selectFieldsProgressAreas,
  selectSelectedFields,
} from '../../Map/slice/selectors';
import { selectResourceTypes } from 'app/containers/Resource/slice/selectors';
import resourceApi from 'app/containers/Resource/resourceApi';
import {
  selectPlannedTaskResourcesUpdates,
  selectThisStageUsedResourcesArr,
  selectTaskListFilters,
  selectTaskResourcesState,
} from './selectors';
import { getTaskListFiltersSearchQuery } from 'utils/helpers';
import { CONFIG } from 'utils/constants';
import { generatePath } from 'react-router-dom';
import {
  mapRecipeResoourcesToTaskPlannedResources,
  mapResourcesToOptionsGroupedByResourceType,
  mapTasksResponseToTasksArr,
} from './helpers';
import { IResourceType } from 'app/containers/Resource/slice/types';

function* getTaskCategoriesSaga(action: PayloadAction<number>) {
  try {
    const selectedEnvId = action.payload;

    const categories: ITaskCategory[] = yield taskApi.getTaskCategories(
      selectedEnvId,
    );

    yield put(actions.setTaskCategories(categories));
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setTaskCategoriesLoading(false));
  }
}

function* getFieldAllTaskListSaga(
  action: PayloadAction<IGetFieldAllTaskListPayload>,
) {
  try {
    yield put(actions.setTaskListLoading(true));
    const {
      envId,
      companyId,
      seasonId,
      fieldId,
      companyGroupId,
    } = action.payload;

    const params: IGetTaskListParams = {
      companyGroupId,
      company_id: companyId || undefined,
      season_id: seasonId || undefined,
      field_season_ids: fieldId || undefined,
    };

    const taskListResponse: ITaskListResponse = yield taskApi.getTasks(
      envId,
      params,
    );

    yield put(
      actions.setFieldAllTaskList({
        allFieldTasksArr: mapTasksResponseToTasksArr(taskListResponse.content),
        fieldId,
      }),
    );
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setTaskListLoading(false));
  }
}

function* getTaskListSaga(action: PayloadAction<IGetTaskListPayload>) {
  try {
    yield put(actions.setTaskListLoading(true));
    const {
      envId,
      filters: { startDate, endDate, status, categoryId },
      companyId,
      seasonId,
      fieldId,
      page,
      companyGroupId,
      sort,
    } = action.payload;

    const params: IGetTaskListParams = {
      companyGroupId,
      company_id: companyId || undefined,
      start_date: startDate,
      end_date: endDate,
      status: status?.length ? status.join(',') : undefined,
      category_ids: categoryId ? String(categoryId) : undefined,
      season_id: seasonId || undefined,
      field_season_ids: fieldId || undefined,
      page: page - 1,
      size: CONFIG.TABLE_PAGE_SIZE,
      sort: sort ? `${sort.name},${sort.order}` : `endDate,desc`,
    };

    let taskListResponse: ITaskListResponse = yield taskApi.getTasks(
      envId,
      params,
    );

    // IF page number is bigger than pages number, then get last page
    if (taskListResponse.totalPages < page) {
      taskListResponse = yield taskApi.getTasks(envId, {
        ...params,
        page: taskListResponse.totalPages - 1,
      });
    }
    const tasksArr: ITasksTableItem[] = mapTasksResponseToTasksArr(
      taskListResponse.content,
    );

    if (fieldId) {
      yield put(
        actions.setFieldTaskList({
          tasksArr,
          fieldId,
          currentPage: taskListResponse.pageable.pageNumber + 1,
          total: taskListResponse.totalElements,
          pageSize: taskListResponse.size,
        }),
      );
    } else {
      yield put(
        actions.setTaskList({
          tasksArr,
          currentPage: taskListResponse.pageable.pageNumber + 1,
          total: taskListResponse.totalElements,
          pageSize: taskListResponse.size,
        }),
      );
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setTaskListLoading(false));
  }
}

function* editTaskSaga(action: PayloadAction<IEditTaskPayload>) {
  try {
    const selectedFieldSeasons: IFieldParameters[] = yield select(
      selectSelectedFields,
    );

    const { task, envId, taskId } = action.payload;

    const {
      newResourcesArr,
      deletedResourcesArr,
      updatedResourcesArr,
    }: {
      newResourcesArr: ITaskPlannedResource[];
      deletedResourcesArr: ITaskPlannedResource[];
      updatedResourcesArr: ITaskPlannedResource[];
    } = yield select(selectPlannedTaskResourcesUpdates);

    const [updatedTask]: [ITask] = yield all([
      taskApi.editTask(envId, task, taskId),
      ...newResourcesArr.map(resource =>
        taskApi.addTaskPlannedResource({
          hiikoEnvironmentId: envId,
          taskId: taskId,
          norm: resource.norm,
          resourceId: resource.resourceId,
        }),
      ),
      ...updatedResourcesArr.map(resource =>
        taskApi.updateTaskPlannedResource({
          hiikoEnvironmentId: envId,
          taskId: taskId,
          norm: resource.norm,
          resourceId: resource.id || 0,
        }),
      ),
      ...deletedResourcesArr.map(resource =>
        taskApi.deleteTaskPlannedResource({
          hiikoEnvironmentId: envId,
          taskId: taskId,
          resourceId: resource.id || 0,
        }),
      ),
    ]);

    const currentFieldsIds = updatedTask.fieldTasks?.map(
      task => task.fieldSeason.id,
    );
    const data = {
      fieldSeasonIds: selectedFieldSeasons
        .filter(field => !currentFieldsIds?.includes(field.fieldId))
        .map(field => field.fieldId),
      removeFieldSeasonIds:
        currentFieldsIds?.filter(
          fieldId =>
            !selectedFieldSeasons.find(field => field.fieldId === fieldId),
        ) || [],
    };

    // send fields data to api only if there is updated fields
    if (data.fieldSeasonIds.length || data.removeFieldSeasonIds.length) {
      yield taskApi.addFields(envId, data, updatedTask.id);
    }

    yield put(
      globalActions.setRedirectTo(
        generatePath(RouteConstants.task.details, {
          envId: Number(envId),
          taskId: updatedTask.id,
        }),
      ),
    );

    yield delay(500);

    notification.success({
      message: i18next?.t(
        'Task.Notification.Updated',
        'Task updated successfully',
      ),
      description: i18next?.t(
        'Task.Notification.CreatedDescription',
        'You can find the task in the task list. You can modify it and record its execution in the task window.',
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setTaskCreateLoading(false));
  }
}

function* getTaskDetailsSaga(action: PayloadAction<IGetTaskDetailsPayload>) {
  try {
    yield put(actions.setTaskDetailsLoading(true));

    const { taskId, envId } = action.payload;

    const [task, notes]: [ITask, string] = yield all([
      taskApi.getTaskDetails(envId, taskId),
      yield taskApi.getTaskNotes(envId, taskId),
    ]);

    yield put(actions.setTaskDetails({ task, notes }));

    const fields =
      task.fieldTasks?.map(fieldTask => fieldTask.fieldSeason.id) || [];
    yield put(mapActions.setSelectedPolygonsIds(fields));

    const fieldsProgress: IFieldProgress[] =
      task.fieldTasks?.map(
        ({ fieldSeason: { id: fieldId, area }, id, progress }) => ({
          fieldTaskId: id,
          fieldId,
          progress,
          area,
        }),
      ) || [];

    yield put(mapActions.setFieldsProgress(fieldsProgress));
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setTaskDetailsLoading(false));
  }
}

function* getTaskProgressSaga(action: PayloadAction<IGetTaskProgressPayload>) {
  try {
    const { taskId, envId } = action.payload;

    const [task, notes]: [ITask, string] = yield all([
      taskApi.getTaskDetails(envId, taskId),
      taskApi.getTaskNotes(envId, taskId),
    ]);

    yield put(actions.setTaskProgress({ task, notes }));

    const fields =
      task.fieldTasks?.map(fieldTask => fieldTask.fieldSeason.id) || [];

    const fieldsProgress: IFieldProgress[] =
      task.fieldTasks?.map(
        ({ fieldSeason: { id: fieldId, area }, id, progress }) => ({
          fieldTaskId: id,
          fieldId,
          progress,
          area,
        }),
      ) || [];

    yield put(mapActions.setSelectedPolygonsIds(fields));

    yield put(mapActions.setFieldsProgress(fieldsProgress));
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setTaskProgressLoading(false));
  }
}

function* updateTaskProgressSaga(
  action: PayloadAction<IUpdateTaskProgressPayload>,
) {
  try {
    const { taskId, envId, fieldTaskProgressUpdateViews } = action.payload;

    yield taskApi.progressUpdate(
      envId,
      { fieldTaskProgressUpdateViews },
      taskId,
    );

    const thisStageUsedResources: ITaskUsedResource[] = yield select(
      selectThisStageUsedResourcesArr,
    );

    const {
      fields,
    }: {
      fields: IFieldProgressWithProgressArea[];
      totalProgressArea: number;
    } = yield select(selectFieldsProgressAreas);

    const usedResourcesObjArr = fields.map(fieldTaskProgress =>
      thisStageUsedResources.map(resourceItem => ({
        resourceId: resourceItem.resourceId,
        amount: resourceItem.norm * fieldTaskProgress.progressArea,
        fieldTaskId: fieldTaskProgress.fieldTaskId,
      })),
    );
    const thisStageData = usedResourcesObjArr.flat(1);

    const {
      usedResources: { updatedResourcesArr },
    } = yield select(selectTaskResourcesState);
    const initialUsedResources: ITaskUsedResource[] = updatedResourcesArr;

    // Merge this stage amount with saved amount
    const payload = thisStageData.map(data => {
      const initialUsedResource = initialUsedResources.find(
        res =>
          res.fieldTaskId === data.fieldTaskId &&
          res.resourceId === data.resourceId,
      );
      if (initialUsedResource?.amount) {
        return { ...data, amount: data.amount + initialUsedResource.amount };
      }
      return data;
    });

    yield taskApi.addTaskUsedResource(payload, envId);

    const taskListFilters = yield select(selectTaskListFilters);
    const taskListFiltersQuery = getTaskListFiltersSearchQuery({
      filters: taskListFilters,
    });

    yield put(
      globalActions.setRedirectTo(
        generatePath(RouteConstants.task.list, {
          envId,
        }).concat(`?${taskListFiltersQuery}`),
      ),
    );

    notification.success({
      message: i18next?.t(
        'Task.Notification.ProgressUpdated',
        'Task progress updated successfully',
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setTaskProgressUpdateLoading(false));
  }
}

function* getTaskPlannedResourcesSaga(
  action: PayloadAction<IGetTaskPlannedResourcesPayload>,
) {
  try {
    const { taskId, envId } = action.payload;

    let taskPlannedResources: ITaskPlannedResource[] = [];

    const task: ITask | undefined = yield taskApi.getTaskDetails(envId, taskId);

    if (
      task &&
      task.recipe &&
      (task.status === 'RECOMMENDED' ||
        task.status === 'DECLINED' ||
        task.status === 'ACCEPTED')
    ) {
      taskPlannedResources = mapRecipeResoourcesToTaskPlannedResources(
        task.recipe.resources,
      );
    } else {
      const taskPlannedResourcesResponse: ITaskPlannedResourceResponseItem[] = yield taskApi.getTaskPlannedResources(
        envId,
        taskId,
      );

      taskPlannedResources = taskPlannedResourcesResponse.map(resource => ({
        id: resource.id,
        resourceId: resource.resource.id,
        norm: resource.norm,
        name: resource.resource.name,
        cultureName: resource.resource.cultureVariation?.culture.name,
        cultureId: resource.resource.cultureVariation?.culture.id,
        recommendedNorm: resource.resource.recommendedNorm,
        unit: resource.resource.type.unit,
      }));
    }

    yield put(actions.setTaskPlannedResources(taskPlannedResources));
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* getTaskUsedResourcesSaga(
  action: PayloadAction<IGetTaskUsedResourcesPayload>,
) {
  try {
    const { taskId, envId } = action.payload;

    const taskUsedResourcesResponse: ITaskUsedResourceResponseItem[] = yield taskApi.getTaskUsedResources(
      envId,
      taskId,
    );

    const taskUsedResources: ITaskUsedResource[] = taskUsedResourcesResponse.map(
      resource => {
        const area =
          resource.fieldTask.fieldSeason.area *
          (resource.fieldTask.progress / 100);

        return {
          id: resource.id,
          resourceId: resource.resource.id,
          norm: resource.amount && area ? resource.amount / area : 0,
          name: resource.resource.name,
          cultureName: resource.resource.cultureVariation?.culture.name,
          cultureId: resource.resource.cultureVariation?.culture.id,
          recommendedNorm: resource.resource.recommendedNorm,
          company: resource.company,
          amount: resource.amount,
          fieldName: resource.fieldTask.fieldSeason.field,
          fieldTaskId: resource.fieldTask.id,

          area,
          unit: resource.resource.type.unit,
        };
      },
    );
    yield put(actions.setTaskUsedResources(taskUsedResources));
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* getAllAvailableTaskResourcesSaga(action: PayloadAction<number>) {
  try {
    const envId = action.payload;

    const resourceTypes: IResourceType[] = yield select(selectResourceTypes);

    const resources: ITaskAvailableResource[][] = yield all(
      resourceTypes.map(type => resourceApi.getResourcesByType(envId, type.id)),
    );

    const resourcesByTypeId: ITaskAvailableResourceOption[] = mapResourcesToOptionsGroupedByResourceType(
      { resourceTypes, resources },
    );

    yield put(actions.setAvailableTaskResources(resourcesByTypeId));
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}
function* setDebouncedRoutesWithoutTaskFiltersSaga(
  action: PayloadAction<IRoutesWithoutTaskFilters>,
) {
  yield delay(700);
  yield put(actions.setRoutesWithoutTaskFilters(action.payload));
}

export function* taskSaga() {
  yield takeLatest(
    actions.setDebouncedRoutesWithoutTaskFilters.type,
    setDebouncedRoutesWithoutTaskFiltersSaga,
  );

  yield takeLatest(
    actions.getAllAvailableTaskResources.type,
    getAllAvailableTaskResourcesSaga,
  );

  yield takeLatest(actions.getTaskUsedResources.type, getTaskUsedResourcesSaga);

  yield takeLatest(
    actions.getTaskPlannedResources.type,
    getTaskPlannedResourcesSaga,
  );

  yield takeLatest(actions.getTaskCategories.type, getTaskCategoriesSaga);

  yield takeLatest(actions.getTaskList.type, getTaskListSaga);
  yield takeLatest(actions.getFieldAllTaskList.type, getFieldAllTaskListSaga);

  yield takeLatest(actions.editTask.type, editTaskSaga);

  yield takeLatest(actions.getTaskDetails.type, getTaskDetailsSaga);
  yield takeLatest(actions.getTaskProgress.type, getTaskProgressSaga);

  yield takeLatest(actions.updateTaskProgress.type, updateTaskProgressSaga);
}
